import { CloseCircleOutlined, CloudFilled } from '@ant-design/icons';
import { Button, Form, Input, Select } from 'antd';
import dayjs from 'dayjs';
import React, { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { dateFormat } from '../../../common/constants';
import { RangePicker } from '../../../components/RangePicker';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import { setFilters } from '../../../store/reducers/orders.slice';
import { assets, statuses, types } from '../Orders.constants';
import { omitDeep } from '../Orders.helpers';
import { GetOrdersRequest } from '../Orders.interfaces';
import styles from './Filters.module.scss';

type Props = {
  filters: GetOrdersRequest;
};

export const Filters: FC<Props> = ({ filters }) => {
  const { currency } = useTypedSelector((state) => state.registration);
  const [form] = Form.useForm();

  const [initialValues, setInitialValues] = React.useState({
    period:
      filters.dateBeg && filters.dateEnd
        ? [dayjs(filters.dateBeg), dayjs(filters.dateEnd)]
        : null,
    filters: {
      currencyId: filters.filters?.currencyId,
      assetType: filters.filters?.assetType,
      orderType: filters.filters?.orderType,
      status: filters.filters?.status,
      number: filters.filters?.number,
      orderbys: filters.orderbys,
    },
    offset: filters.offset,
    limit: filters.limit,
  });

  const dispatch = useDispatch();

  const onReset = () => {
    form.resetFields(['period']);

    dispatch(
      setFilters({
        offset: 0,
        limit: 10,
      }),
    );

    setInitialValues({
      period: null,
      filters: {
        currencyId: undefined,
        assetType: undefined,
        orderType: undefined,
        status: undefined,
        number: undefined,
        orderbys: undefined,
      },
      offset: 0,
      limit: 10,
    });

    form.setFieldsValue(initialValues);
  };

  const onChangeFilters = (value: any, allValues: GetOrdersRequest) => {
    const objFilters = {
      ...allValues,
      offset: 0,
      limit: 10,
    };

    if (value && value.period) {
      const [start, end] = value.period;
      const adjustedEnd = end ? end.endOf('day') : null;

      objFilters.dateBeg = dayjs.utc(start).tz('Europe/Moscow').format();
      objFilters.dateEnd = dayjs.utc(adjustedEnd).tz('Europe/Moscow').format();

      delete objFilters.period;
    } else if (value === null) {
      // handle explicit null case
      //Optionally reset dateBeg and dateEnd to null or default values
      delete objFilters.dateBeg;
      delete objFilters.dateEnd;
      delete objFilters.period;
    }

    // Приводим в строку, потому что в store нельзя Dayjs тип
    if (allValues.period) {
      objFilters.dateBeg = dayjs
        .utc(allValues.period[0])
        .tz('Europe/Moscow')
        .format();
      objFilters.dateEnd = dayjs
        .utc(allValues.period[1])
        .tz('Europe/Moscow')
        .format();
      delete objFilters.period;
    }

    // Очищаем объект filters от undefined
    const updatedFilters = omitDeep(objFilters);
    dispatch(setFilters(updatedFilters));
  };

  return (
    <Form
      initialValues={initialValues}
      className={styles.container}
      onValuesChange={onChangeFilters}
      form={form}
    >
      <div className={styles.filters}>
        <Form.Item className={styles['filters-item__period']} name="period">
          <RangePicker
            format={dateFormat}
            placeholder={['Дата "от"', 'Дата "до"']}
          />
        </Form.Item>

        <Form.Item
          className={styles['filters-item__status']}
          name={['filters', 'status']}
        >
          <Select
            value={initialValues.filters.status}
            options={statuses}
            allowClear
            removeIcon
            placeholder="Статус"
          />
        </Form.Item>

        <Form.Item
          className={styles['filters-item__number']}
          name={['filters', 'number']}
        >
          <Input type="number" allowClear placeholder="Номер" />
        </Form.Item>

        <Form.Item
          className={styles['filters-item__type']}
          name={['filters', 'orderType']}
        >
          <Select options={types} allowClear placeholder="Тип заявки" />
        </Form.Item>

        <Form.Item
          className={styles['filters-item__asset']}
          name={['filters', 'assetType']}
        >
          <Select
            showSearch
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? '')
                .toLowerCase()
                .localeCompare((optionB?.label ?? '').toLowerCase())
            }
            options={assets}
            allowClear
            placeholder="Актив"
          />
        </Form.Item>

        <Form.Item
          className={styles['filters-item__currency']}
          name={['filters', 'currencyId']}
        >
          <Select options={currency} allowClear placeholder="Валюта" />
        </Form.Item>

        <Button icon={<CloseCircleOutlined />} onClick={onReset} type="text">
          Очистить все
        </Button>
      </div>
    </Form>
  );
};
